import React, {useCallback, useEffect, useRef, useState} from 'react';
import * as Sentry from '@sentry/browser';
import css from './ReportsV2.module.scss';
import {Dropdown, DropdownButton} from 'react-bootstrap';
import {UserInfo, usePulsoidClient} from '../../PulsoidClient';
import {ReportsV2Statistics} from './ReportsV2Statistics/ReportsV2Statistics';
import moment from 'moment';
import {GetStreamReportsResponse, Report} from '../../types/Api/Reports';
import {
  ReportsV2List,
  ReportsV2ListLoader,
} from './ReportsV2List/ReportsV2List';
import {ReportsV2EmptyState} from './ReportsV2EmptyState/ReportsV2EmptyState';

enum StatisticsPeriods {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
  ALL = 'all',
}

const StatisticsPeriodsLabelsMap = {
  [StatisticsPeriods.DAY]: 'Last Day',
  [StatisticsPeriods.WEEK]: 'Last Week',
  [StatisticsPeriods.MONTH]: 'Last Month',
  [StatisticsPeriods.YEAR]: 'Last Year',
  [StatisticsPeriods.ALL]: 'All time',
};

const DEFAULT_PAGE = {page: 1, size: 20};

export const ReportsV2 = () => {
  const pulsoidClient = usePulsoidClient();
  const [period, setPeriod] = useState<StatisticsPeriods>(
    StatisticsPeriods.MONTH
  );
  const [loading, setLoading] = useState(true);
  const [streamReportPage, setStreamReportPage] =
    useState<GetStreamReportsResponse['page']>();
  const [profile, setProfile] = useState<UserInfo>();
  const [reports, setReports] = useState<Report[]>([]);
  const currentTime = useRef(moment()).current;

  const isReportListEmpty = !reports.length && !loading;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, streamReportRes] = await Promise.all([
        pulsoidClient.getUserInfo(),
        pulsoidClient.getStreamReports(DEFAULT_PAGE),
      ]);

      setProfile(profileRes);
      setStreamReportPage(streamReportRes.page);
      setReports([...reports, ...streamReportRes.reports]);
    } catch (error) {
      Sentry.captureException(error);
    }
    setLoading(false);
  };

  const loadMoreReports = useCallback(async () => {
    if (
      !streamReportPage ||
      streamReportPage.total <= streamReportPage.page * streamReportPage.size
    ) {
      return;
    }

    try {
      const streamReportRes = await pulsoidClient.getStreamReports({
        page: streamReportPage.page + 1,
        size: streamReportPage.size,
      });
      setStreamReportPage(streamReportRes.page);
      setReports([...reports, ...streamReportRes.reports]);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [streamReportPage, reports]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <main className={css.root}>
      <header className={css.header}>
        <h1>Reports</h1>
      </header>

      <div className={css.content}>
        <div className={css.statistics}>
          <div className={css.contentHeader}>
            <h4>Summary</h4>

            <DropdownButton
              title={StatisticsPeriodsLabelsMap[period]}
              variant="secondary"
              className={css.periodDropdown}
            >
              {Object.values(StatisticsPeriods).map((value) => (
                <Dropdown.Item
                  className={css.periodDropdownItem}
                  key={value}
                  onClick={() => setPeriod(value)}
                  active={period === value}
                >
                  {StatisticsPeriodsLabelsMap[value]}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </div>

          <ReportsV2Statistics
            from={
              period === StatisticsPeriods.ALL
                ? null
                : moment(currentTime).subtract(1, period).toISOString()
            }
            to={currentTime.toISOString()}
          />
        </div>

        <div className={css.reports}>
          <div className={css.contentHeader}>
            <h4>Reports</h4>
          </div>

          <div className={css.reportsContent}>
            {isReportListEmpty ? (
              <ReportsV2EmptyState
                type={
                  profile?.isTwitchConnected
                    ? 'start-streaming'
                    : 'connect-twitch'
                }
              />
            ) : loading ? (
              <ReportsV2ListLoader />
            ) : (
              <ReportsV2List reports={reports} onLoadMore={loadMoreReports} />
            )}
          </div>
        </div>
      </div>
    </main>
  );
};
